// src/components/inventory/PrintPreviewModal.js
//
// --- UPDATE (Inventory MOW+ v2) ----------------------------------------------------------
//   • Fixed: an item without qr_code_url stayed on "Loading QR Code..." forever (the effect only
//     ran when a QR URL existed); it now shows a SKU label that can still be printed.
//   • Copies (1–50) and label size (standard / small bin label); preview shows the real label
//     (SKU, location, category, FlowSense MOW+ line) from PrintableQRCode.
//   • Printing works with react-to-print v2 (ReactToPrint) and, when that component is not in
//     the installed version, with a built-in print window (escaped HTML, the QR as a data URL).
//   • A failed QR download says so and still allows a text label.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Icon,
  Spinner,
  Box,
  Image, // Import Image for the embedded component
  HStack, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, RadioGroup, Radio, FormControl, FormLabel, Alert, AlertIcon, // --- UPDATE
} from '@chakra-ui/react';
import * as ReactToPrintModule from 'react-to-print'; // --- UPDATE: was `import ReactToPrint from 'react-to-print'` (v3 has no default component)
import { MdPrint } from 'react-icons/md';
import { config } from '../../config';
import SharedLabel from './PrintableQRCode'; // --- UPDATE

// --- UPDATE: v2 exports the component as default (and named), v3 only the hook
const ReactToPrint = ReactToPrintModule.default && typeof ReactToPrintModule.default !== 'object' ? ReactToPrintModule.default : ReactToPrintModule.ReactToPrint || null;
const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
export function labelHtml(item, qr, copies = 1, size = 'standard') {
  const small = size === 'small';
  const sku = item.sku || `ITM-${String(item.id).padStart(4, '0')}`;
  const meta = [item.warehouse_location || item.location, item.category_name || item.category].filter(Boolean).join(' · ');
  const one = `<div class="lbl"><h1>${esc(item.name)}</h1>${qr ? `<img src="${esc(qr)}" alt="QR"/>` : `<div class="sku-big">${esc(sku)}</div>`}<p class="sku">${esc(sku)}</p>${meta ? `<p>${esc(meta)}</p>` : ''}<p class="brand">FlowSense MOW+ · Ministry of Works</p></div>`;
  return `<!doctype html><html><head><meta charset="utf-8"><title>Labels - ${esc(sku)}</title><style>
    body{font-family:Arial,sans-serif;margin:8mm;display:flex;flex-wrap:wrap;gap:6mm}
    .lbl{border:1.5px solid #000;border-radius:8px;padding:${small ? '3mm' : '6mm'};width:${small ? '48mm' : '75mm'};text-align:center;page-break-inside:avoid}
    h1{font-size:${small ? '12pt' : '16pt'};margin:0 0 2mm} img{width:${small ? '32mm' : '55mm'};height:auto}
    .sku{font-family:monospace;font-weight:700;margin:1mm 0} .sku-big{font-family:monospace;font-weight:900;font-size:${small ? '16pt' : '26pt'};border:2px dashed #888;padding:6mm 2mm}
    p{margin:1mm 0;font-size:9pt} .brand{font-size:7pt;letter-spacing:1px;text-transform:uppercase;color:#555}
  </style></head><body>${Array.from({ length: Math.max(1, copies) }, () => one).join('')}<script>window.onload=function(){window.print();setTimeout(function(){window.close()},300)}</script></body></html>`;
}
/** Many labels at once (Quick actions → Print labels): QR images load from the server's public URL. */
export function bulkLabelsHtml(items, baseUrl) {
  const one = (item) => {
    const sku = item.sku || `ITM-${String(item.id).padStart(4, '0')}`;
    const qr = item.qr_code_url ? (/^https?:/i.test(item.qr_code_url) ? item.qr_code_url : `${baseUrl}${item.qr_code_url}`) : null;
    const meta = [item.warehouse_location, item.category_name].filter(Boolean).join(' · ');
    return `<div class="lbl"><h1>${esc(item.name)}</h1>${qr ? `<img src="${esc(qr)}" alt="QR"/>` : `<div class="sku-big">${esc(sku)}</div>`}<p class="sku">${esc(sku)}</p>${meta ? `<p>${esc(meta)}</p>` : ''}<p class="brand">FlowSense MOW+ · Ministry of Works</p></div>`;
  };
  return labelHtml({ id: 0, name: '' }, null, 0).replace(/<body>[\s\S]*<script>/, `<body>${(items || []).map(one).join('')}<script>`).replace('<title>Labels - ITM-0000</title>', `<title>FlowSense labels (${(items || []).length})</title>`);
}
// --- END UPDATE

// --- PrintableQRCode Component is now defined inside this file ---
const PrintableQRCode = React.forwardRef(({ item, baseUrl, qrCodeDataUrl }, ref) => {
  if (!item) {
    return null;
  }

  return (
    <Box ref={ref} p={4} borderWidth="1px" borderRadius="lg" m={4} w="300px">
      <VStack spacing={4}>
        <Text fontSize="xl" fontWeight="bold" textAlign="center">
          {item.name}
        </Text>
        <Image
          src={qrCodeDataUrl || `${baseUrl}${item.qr_code_url}`}
          alt={`${item.name} QR Code`}
          boxSize="250px"
        />
        <Text fontSize="sm" color="gray.600">
          ID: {item.id} | Supplier: {item.supplier || 'N/A'}
        </Text>
      </VStack>
    </Box>
  );
});
PrintableQRCode.displayName = 'EmbeddedPrintableQRCode'; // --- UPDATE
export { PrintableQRCode as EmbeddedPrintableQRCode }; // --- UPDATE: the preview now uses the shared label; kept for other imports
// --- End of embedded component ---


const PrintPreviewModal = ({ isOpen, onClose, item }) => {
  const componentRef = useRef();
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  // --- UPDATE
  const [copies, setCopies] = useState(1);
  const [size, setSize] = useState('standard');
  const [qrError, setQrError] = useState(false);
  // --- END UPDATE

  const baseUrl = config.apiUrl.replace(/\/api\/v1$/, '');

  useEffect(() => {
    // --- UPDATE: no QR image → stop loading and print a SKU label
    if (isOpen && item && !item.qr_code_url) { setQrCodeDataUrl(''); setIsLoading(false); setQrError(false); return undefined; }
    if (isOpen) { setCopies(1); setQrError(false); }
    let alive = true;
    // --- END UPDATE
    if (isOpen && item && item.qr_code_url) {
      setIsLoading(true);
      setQrCodeDataUrl(''); // Reset on item change

      const imageUrl = /^https?:/i.test(item.qr_code_url) ? item.qr_code_url : `${baseUrl}${item.qr_code_url}`; // --- UPDATE: absolute URLs too

      fetch(imageUrl)
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.blob();
        })
        .then(blob => {
          const reader = new FileReader();
          reader.onloadend = () => {
            if (!alive) return; // --- UPDATE
            setQrCodeDataUrl(reader.result);
            setIsLoading(false);
          };
          reader.readAsDataURL(blob);
        })
        .catch(error => {
          console.error('Error fetching QR code for printing:', error);
          if (alive) { setQrError(true); setIsLoading(false); } // --- UPDATE
        });
    }
    return () => { alive = false; }; // --- UPDATE
  }, [item, isOpen, baseUrl]);

  // --- UPDATE: built-in print (copies, sizes) — also the fallback when ReactToPrint is not available
  const printNow = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(labelHtml(item, qrCodeDataUrl || null, Number(copies) || 1, size));
    w.document.close();
    onClose?.();
  };
  // --- END UPDATE

  if (!item) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Print QR Code Label</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack>
            <Text mb={4}>Review the label below. Click "Print Label" to open the print dialog.</Text>
            {/* --- UPDATE: copies + size */}
            <HStack w="full" align="flex-end" spacing={4}>
              <FormControl maxW="120px"><FormLabel fontSize="sm">Copies</FormLabel>
                <NumberInput size="sm" min={1} max={50} value={copies} onChange={(v) => setCopies(v)}><NumberInputField aria-label="Copies" /><NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper></NumberInput></FormControl>
              <FormControl><FormLabel fontSize="sm">Size</FormLabel>
                <RadioGroup value={size} onChange={setSize}><HStack><Radio value="standard">Standard</Radio><Radio value="small">Small (bin)</Radio></HStack></RadioGroup></FormControl>
            </HStack>
            {qrError && <Alert status="warning" borderRadius="lg" fontSize="sm"><AlertIcon />The QR image could not be loaded — a SKU label will be printed instead.</Alert>}
            {!item.qr_code_url && <Alert status="info" borderRadius="lg" fontSize="sm"><AlertIcon />This item has no QR code yet — a SKU label will be printed.</Alert>}
            {/* --- END UPDATE */}
            {isLoading ? (
              <Box boxSize="250px" display="flex" alignItems="center" justifyContent="center">
                <VStack>
                  <Spinner />
                  <Text>Loading QR Code...</Text>
                </VStack>
              </Box>
            ) : ( /* --- UPDATE: always a printable label (QR or SKU) */
              <SharedLabel
                ref={componentRef}
                item={item}
                qrCodeDataUrl={qrCodeDataUrl}
                size={size}
              />
            )}
            {Number(copies) > 1 && <Text fontSize="xs" color="gray.500">× {copies} copies</Text>}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          {/* --- UPDATE: ReactToPrint for one standard label when available, otherwise the built-in print */}
          {ReactToPrint && Number(copies) === 1 && size === 'standard' ? (
          <ReactToPrint
            trigger={() => (
              <Button colorScheme="blue" leftIcon={<Icon as={MdPrint} />} disabled={isLoading}>
                Print Label
              </Button>
            )}
            content={() => componentRef.current}
            onAfterPrint={onClose}
          />
          ) : (
            <Button colorScheme="blue" leftIcon={<Icon as={MdPrint} />} isDisabled={isLoading} onClick={printNow}>
              {Number(copies) > 1 ? `Print ${copies} Labels` : 'Print Label'}
            </Button>
          )}
          {/* --- END UPDATE */}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PrintPreviewModal;
